import { defineComponent as _defineComponent } from 'vue'
import { ref, onMounted } from 'vue'

  import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
  import KetchUpButton from '@/components/common/KetchUpButton.vue'

  import { useRoute, useRouter } from 'vue-router/composables'
  import useAuthentication from '@/composables/useAuthentication'

  import CookieHelper from '@/helpers/CookieHelper'

  import { UserModule } from '@/store/modules/user'

  import * as jose from 'jose'
  import auth_config from '../../auth_config.json'
  import * as Sentry from '@sentry/vue'

  
export default /*#__PURE__*/_defineComponent({
  __name: 'MagicLogin',
  setup(__props) {

  const route = useRoute()
  const router = useRouter()
  const { tokenKey } = useAuthentication()

  const { staging, production } = auth_config
  const domain = process.env.APP_ENV === 'production' ? production.domain : staging.domain
  const audience = process.env.APP_ENV === 'production' ? production.audience : staging.audience

  const loading = ref(true)
  const error = ref('')

  const goToLogin = () => {
    router.push('/login')
  }

  const validateAuth0Token = async (token: string) => {
    try {
      // For Auth0 tokens, we can validate them by decoding and checking the issuer
      const decodedToken = jose.decodeJwt(token)

      // Check if it's an Auth0 token by verifying the issuer
      if (decodedToken.iss && typeof decodedToken.iss === 'string') {
        const expectedIssuer = `https://${domain}/`
        if (!decodedToken.iss.startsWith(expectedIssuer)) {
          throw new Error('Invalid Auth0 token issuer')
        }
      }

      // Check audience if present
      if (decodedToken.aud && decodedToken.aud !== audience) {
        throw new Error('Invalid token audience')
      }

      return decodedToken
    } catch (err) {
      throw new Error('Invalid Auth0 token')
    }
  }

  const validateCustomJWT = async (token: string) => {
    try {
      // For custom JWT tokens, we just decode and validate basic structure
      const decodedToken = jose.decodeJwt(token)

      if (!decodedToken?.sub) {
        throw new Error('Invalid custom JWT: missing user ID')
      }

      return decodedToken
    } catch (err) {
      throw new Error('Invalid custom JWT token')
    }
  }

  const validateAndSetToken = async (token: string) => {
    try {
      let decodedToken

      // Try to validate as Auth0 token first
      try {
        decodedToken = await validateAuth0Token(token)
      } catch (auth0Error) {
        // If Auth0 validation fails, try as custom JWT
        try {
          decodedToken = await validateCustomJWT(token)
        } catch (customError) {
          throw new Error('Token is neither a valid Auth0 token nor a valid custom JWT')
        }
      }

      if (!decodedToken?.sub) {
        throw new Error('Invalid token: missing user ID')
      }

      // Check if token is expired
      if (decodedToken.exp && Date.now() >= decodedToken.exp * 1000) {
        throw new Error('Token has expired')
      }

      CookieHelper.setCrossDomainCookie(tokenKey.value, token)

      await UserModule.getUser()

      window.location.href = '/'
    } catch (err) {
      console.error('Magic login error:', err)

      Sentry.captureException(err, {
        tags: {
          component: 'MagicLogin',
          action: 'validateAndSetToken',
        },
        extra: {
          hasToken: !!route.query.token,
          tokenLength: route.query.token ? (route.query.token as string).length : 0,
          redirectUrl: route.query.redirect,
        },
      })

      error.value = err instanceof Error ? err.message : 'Invalid or expired token'
      loading.value = false
    }
  }

  onMounted(async () => {
    const token = route.query.token as string

    if (!token) {
      error.value = 'No token provided. Please check your magic login link.'
      loading.value = false
      return
    }

    if (token.split('.').length !== 3) {
      error.value = 'Invalid token format. Token must be a valid JWT.'
      loading.value = false
      return
    }

    await validateAndSetToken(token)
  })

return { __sfc: true,route, router, tokenKey, staging, production, domain, audience, loading, error, goToLogin, validateAuth0Token, validateCustomJWT, validateAndSetToken, ref, onMounted, LoadingSpinner, KetchUpButton, useRoute, useRouter, useAuthentication, CookieHelper, UserModule, jose, auth_config, Sentry }
}

})